import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Eye,
  X,
  Search,
  ReceiptText,
} from "lucide-react";
import { BASE_URL } from "../api";

const API_BASE = `${BASE_URL}/api`;

/**
 * Paid Family Transactions — /billing/paid-family-transactions
 *
 * Backend: clients/views/rural_health_package/paid_family_transaction_views.py
 *   GET /paid-family-transactions/ -> { success, count, transactions: [...] }
 *   GET /paid-family-transactions/?family_id=&package_id=&status=&payment_transaction_id=
 *   GET /paid-family-transactions/<transaction_id>/ -> { success, transaction: {...} }
 * Backend caps list at 500 rows (order_by -created_at). No server-side pagination.
 * Auth: Authorization: Token <auth_token> (existing localStorage "token" pattern).
 */

// ---------------------------------------------------------------------------
// Helpers (same conventions as MfiFinanceActivation.jsx / TcsBilling.jsx)
// ---------------------------------------------------------------------------
function toArrayPayload(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.transactions)) return data.transactions;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.transaction)) return data.transaction;
  if (data.transaction && typeof data.transaction === "object") return [data.transaction];
  return [];
}

function formatAmount(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normStatus(s) {
  return String(s || "").trim().toUpperCase();
}

function statusBadgeClass(status) {
  const s = normStatus(status);
  if (s === "SUCCESS" || s === "PAID") return "bg-emerald-100 text-emerald-700";
  if (s === "PENDING") return "bg-amber-100 text-amber-700";
  if (s === "FAILED" || s === "FAILURE") return "bg-red-100 text-red-700";
  return "bg-slate-200 text-slate-600";
}

// ---------------------------------------------------------------------------
// Auth + request helper (reuses existing localStorage token pattern)
// ---------------------------------------------------------------------------
const getAuthHeader = (token) => {
  if (!token) return {};
  if (token.split(".").length === 3) return { Authorization: `Bearer ${token}` };
  return { Authorization: `Token ${token}` };
};

const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Authentication required. Please login again.");

  const headers = {
    ...getAuthHeader(token),
    ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem("token");
    throw new Error("Session expired. Please login again.");
  }
  if (res.status === 403) {
    throw new Error("Access denied. Please contact administrator.");
  }
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const err = await res.json();
      msg = err.message || err.detail || err.error || JSON.stringify(err);
    } catch {
      // keep default
    }
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

// ---------------------------------------------------------------------------
// Toast (same pattern as MfiFinanceActivation.jsx)
// ---------------------------------------------------------------------------
function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [toast, onClose]);

  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div
      className={`fixed top-5 right-5 z-[60] flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg max-w-sm ${
        isError ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"
      }`}
      role="status"
    >
      {isError ? (
        <XCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
      ) : (
        <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
      )}
      <div className={`text-sm font-medium ${isError ? "text-red-800" : "text-emerald-800"}`}>{toast.message}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal — GET /paid-family-transactions/<transaction_id>/
// ---------------------------------------------------------------------------
function PaidTxDetailModal({ transactionId, detail, loading, error, onClose, onRetry }) {
  useEffect(() => {
    if (!transactionId) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [transactionId, onClose]);

  if (!transactionId) return null;
  const t = detail || null;
  const rows = t
    ? [
        ["Database ID", t.id != null ? String(t.id) : "—"],
        ["Transaction ID", t.transaction_id || "—"],
        ["Family ID", t.family?.family_id || "—"],
        ["Family database ID", t.family?.id != null ? String(t.family.id) : "—"],
        ["Package database ID", t.package?.id != null ? String(t.package.id) : "—"],
        ["Amount", formatAmount(t.amount)],
        ["Payment Transaction ID", t.payment_transaction_id || "—"],
        ["Wallet Debit Transaction ID", t.wallet_debit_transaction_id || "—"],
        ["Status", t.status || "—"],
        ["Paid At", formatDate(t.paid_at)],
        ["Paid By ID", t.paid_by_id != null ? String(t.paid_by_id) : "—"],
        ["Remarks", t.remarks || "—"],
        ["Created At", formatDate(t.created_at)],
        ["Updated At", formatDate(t.updated_at)],
      ]
    : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-800">{transactionId}</h3>
            <p className="text-xs text-slate-500 mt-0.5">Paid family transaction detail</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5">
          {loading && (
            <div className="py-10 text-center text-slate-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Loading transaction detail...
            </div>
          )}
          {!loading && error && (
            <div className="py-6 text-center">
              <div className="text-sm text-red-600 mb-3">{error}</div>
              <button onClick={onRetry} className="px-4 py-1.5 rounded-lg bg-white border border-slate-200 text-sm hover:bg-slate-50">
                Retry
              </button>
            </div>
          )}
          {!loading && !error && t && (
            <dl className="text-sm space-y-2">
              {rows.map(([k, v]) => (
                <div key={k} className="flex gap-3">
                  <dt className="text-slate-400 w-48 shrink-0">{k}</dt>
                  <dd className="text-slate-800 break-all">{String(v)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status options — backend validates against PaidFamilyTransaction.STATUS_CHOICES.
// Backend STATUS_CHOICES includes PAID (seen live: "status": "PAID").
// Summary counts PAID + SUCCESS as Successful.

// ---------------------------------------------------------------------------
// Reusable content (used standalone AND embedded as MFI Finance tab).
// No page header / outer layout here so it fits inside the MFI tab card.
// ---------------------------------------------------------------------------
export function PaidFamilyTransactionsContent({ selectedPackageId = "", selectedClientId = "" } = {}) {
  // Auto query uses ONLY the MFI section's selected package.
  // Backend supports family_id / package_id / status / payment_transaction_id,
  // but this tab keeps just package_id from above + a frontend date filter
  // on paid_at (backend has no date param). Client is context only.
  const [dateFilter, setDateFilter] = useState(""); // YYYY-MM-DD

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const notify = (message, type = "success") => setToast({ message, type });

  // Detail modal
  const [detailTxId, setDetailTxId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Prevent duplicate concurrent calls + repeat of identical query
  const loadingRef = useRef(false);
  const lastQueryRef = useRef(null);

  const buildQuery = useCallback((f) => {
    const params = new URLSearchParams();
    const pkg = String(f.package_id || "").trim();
    if (pkg) params.set("package_id", pkg);
    return params.toString();
  }, []);

  const loadTransactions = useCallback(
    async (filters = {}, force = false) => {
      const qs = buildQuery(filters);
      if (!force && lastQueryRef.current === qs && loadingRef.current) return;
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      try {
        const data = await apiRequest(`${API_BASE}/paid-family-transactions/${qs ? `?${qs}` : ""}`);
        setTransactions(toArrayPayload(data));
        lastQueryRef.current = qs;
      } catch (err) {
        setError(err.message || "Could not load paid family transactions");
        setTransactions([]);
        if (String(err.message || "").toLowerCase().includes("session expired")) {
          notify(err.message, "error");
        }
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [buildQuery]
  );

  // Initial load — uses the parent-selected package when present
  useEffect(() => {
    const initialPkg = String(selectedPackageId || "").trim();
    loadTransactions(
      initialPkg ? { package_id: initialPkg } : {},
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTransactions]);

  // When the MFI section's selected package changes, auto reload.
  const selectedPkgRef = useRef(String(selectedPackageId || ""));
  useEffect(() => {
    const nextPkg = String(selectedPackageId || "").trim();
    if (nextPkg === selectedPkgRef.current) return;
    selectedPkgRef.current = nextPkg;
    loadTransactions(nextPkg ? { package_id: nextPkg } : {}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackageId]);

  const handleRetry = () => {
    const pkg = String(selectedPackageId || "").trim();
    loadTransactions(pkg ? { package_id: pkg } : {}, true);
  };

  // Date-only frontend filter on paid_at (fallback created_at).
  // Backend has no date query param, so this never triggers an API call.
  const toDateKey = (value) => {
    if (!value) return "";
    const s = String(value);
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const filteredTransactions = useMemo(() => {
    if (!dateFilter) return transactions;
    return transactions.filter((t) => toDateKey(t.paid_at || t.created_at) === dateFilter);
  }, [transactions, dateFilter]);

  const loadDetail = useCallback(async (transactionId) => {
    if (!transactionId) return;
    setLoadingDetail(true);
    setDetailError(null);
    try {
      const data = await apiRequest(
        `${API_BASE}/paid-family-transactions/${encodeURIComponent(transactionId)}/`
      );
      const tx = data?.transaction || null;
      if (!tx) throw new Error(data?.message || "Paid-family transaction not found.");
      setDetail(tx);
    } catch (err) {
      setDetail(null);
      setDetailError(err.message || "Paid-family transaction not found.");
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const handleView = (row) => {
    const txId = row?.transaction_id || (row?.id != null ? String(row.id) : "");
    if (!txId) return notify("Missing transaction ID", "error");
    setDetail(null);
    setDetailError(null);
    setDetailTxId(String(txId));
    loadDetail(String(txId));
  };

  const closeDetail = () => {
    setDetailTxId(null);
    setDetail(null);
    setDetailError(null);
  };

  // Summary cards — computed from the visible (date-filtered) rows.
  const summary = useMemo(() => {
    let success = 0;
    let pending = 0;
    let failed = 0;
    for (const t of filteredTransactions) {
      const s = normStatus(t.status);
      if (s === "SUCCESS" || s === "PAID") success += 1;
      else if (s === "PENDING") pending += 1;
      else if (s === "FAILED") failed += 1;
    }
    return { total: filteredTransactions.length, success, pending, failed };
  }, [filteredTransactions]);

  const cards = [
    { label: "Total Transactions", value: summary.total, cls: "bg-indigo-50 text-indigo-700" },
    { label: "Successful Transactions", value: summary.success, cls: "bg-emerald-50 text-emerald-700" },
    { label: "Pending Transactions", value: summary.pending, cls: "bg-amber-50 text-amber-700" },
    { label: "Failed Transactions", value: summary.failed, cls: "bg-red-50 text-red-700" },
  ];

  return (
    <div className="space-y-6">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <PaidTxDetailModal
        transactionId={detailTxId}
        detail={detail}
        loading={loadingDetail}
        error={detailError}
        onClose={closeDetail}
        onRetry={() => detailTxId && loadDetail(detailTxId)}
      />

      {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl shadow-sm border border-slate-200 px-5 py-4">
              <div className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium mb-2 ${c.cls}`}>
                {c.label}
              </div>
              <div className="text-2xl font-semibold text-slate-800">
                {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : c.value}
              </div>
            </div>
          ))}
        </div>

        {/* Date filter only (frontend, on Paid At) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-[200px] max-w-xs">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Filter by Date (Paid At)</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            {dateFilter && (
              <button
                onClick={() => setDateFilter("")}
                className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"
              >
                Clear Date
              </button>
            )}
            {dateFilter && (
              <span className="text-xs text-slate-500 whitespace-nowrap pb-2">
                {filteredTransactions.length} of {transactions.length} shown
              </span>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100 bg-slate-50/60">
            <span className="text-xs text-slate-500">
              {loading
                ? "Loading..."
                : dateFilter
                  ? `${filteredTransactions.length} of ${transactions.length} transaction(s) on ${dateFilter}`
                  : transactions.length > 0
                    ? `${transactions.length} transaction(s) (max 500 per request)`
                    : "No transactions"}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1100px]">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="px-4 py-3 font-medium">S. No.</th>
                  <th className="px-4 py-3 font-medium">Transaction ID</th>
                  <th className="px-4 py-3 font-medium">Family ID</th>
                  <th className="px-4 py-3 font-medium">Package ID</th>
                  <th className="px-4 py-3 font-medium text-right">Amount</th>
                  <th className="px-4 py-3 font-medium">Payment Txn ID</th>
                  <th className="px-4 py-3 font-medium">Wallet Debit Txn</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Paid At</th>
                  <th className="px-4 py-3 font-medium">Paid By</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={11} className="px-6 py-10 text-center text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                      Loading paid family transactions...
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td colSpan={11} className="px-6 py-10 text-center">
                      <div className="text-sm text-red-600 mb-2">{error}</div>
                      <button onClick={handleRetry} className="px-4 py-1.5 rounded-lg bg-white border border-slate-200 text-sm hover:bg-slate-50">
                        Retry
                      </button>
                    </td>
                  </tr>
                )}
                {!loading && !error && filteredTransactions.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-6 py-10 text-center text-slate-400">
                      {dateFilter
                        ? `No paid family transactions on ${dateFilter}.`
                        : "No paid family transactions found for the selected package."}
                    </td>
                  </tr>
                )}
                {!loading &&
                  !error &&
                  filteredTransactions.map((t, idx) => (
                    <tr key={String(t.id ?? t.transaction_id ?? idx)} className="border-t border-slate-100 hover:bg-slate-50/60 align-top">
                      <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{t.transaction_id || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{t.family?.family_id || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{t.package?.id != null ? String(t.package.id) : "—"}</td>
                      <td className="px-4 py-3 text-right font-medium text-slate-800">{formatAmount(t.amount)}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{t.payment_transaction_id || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{t.wallet_debit_transaction_id || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(t.status)}`}>
                          {t.status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{formatDate(t.paid_at)}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{t.paid_by_id != null ? String(t.paid_by_id) : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleView(t)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          <Eye className="w-3.5 h-3.5" /> View Details
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Standalone page wrapper (kept for backward compat; route now removed in
// favor of the MFI Finance Activation tab — renders same content with header)
// ---------------------------------------------------------------------------
export default function PaidFamilyTransactionsPage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-24" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="bg-[#0D3366] text-white shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
            <ReceiptText className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold leading-tight">Paid Family Transactions</h1>
            <p className="text-indigo-100 text-xs">View and manage family package payment transaction history.</p>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 py-8">
        <PaidFamilyTransactionsContent />
      </div>
    </div>
  );
}
